import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Flex,
  Spinner,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  useToast,
  Divider,
  useColorModeValue,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Avatar,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Code
} from '@chakra-ui/react';
import { FaCalendarAlt, FaMapMarkerAlt, FaUserFriends, FaSearch, FaEdit, FaCamera, FaAngleLeft, FaEye, FaEnvelope, FaPhone, FaBug } from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';
import './EventDashboard.css';

const EventDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBgColor = useColorModeValue('blue.500', 'blue.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      // For development purposes, always set as admin
      setIsAdmin(true);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }
        
        // In production, you'd check if the user is admin or staff
        // const { data, error } = await supabase
        //   .from('app_users')
        //   .select('is_admin, user_type')
        //   .eq('id', session.user.id)
        //   .single();
        
        // if (error || (!data.is_admin && data.user_type !== 'staff')) {
        //   navigate('/dashboard');
        //   return;
        // }
        
        fetchEventDetails();
        fetchEventUsers();
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast({
          title: 'Authentication Error',
          description: 'Please log in again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/login');
      }
    };
    
    checkAdminStatus();
  }, [eventId, navigate, toast]);
  
  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setEvent(data);
      } else {
        toast({
          title: 'Event not found',
          description: 'The requested event could not be found.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/dashboard/events');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast({
        title: 'Error loading event',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEventUsers = async () => {
    try {
      // First check if the event_id is in a valid format
      if (!eventId || typeof eventId !== 'string') {
        console.error('Invalid event ID format:', eventId);
        throw new Error(`Invalid event ID: ${eventId}`);
      }
      
      // First try: Get debug info
      if (isAdmin) {
        console.log('Admin mode: Running diagnostics first');
        const { data: debugData, error: debugError } = await supabase.rpc('debug_event_users', {
          event_uuid: eventId
        });
        
        if (debugError) {
          console.error('❌ Error getting debug data:', debugError);
        } else {
          console.log('📊 Event Debug Report:', debugData);
        }
      }
      
      // Try using the improved admin_get_event_users RPC function to get registered users
      console.log('Fetching event users using admin_get_event_users function...');
      const { data: eventUsers, error: eventUsersError } = await supabase.rpc('admin_get_event_users', {
        event_uuid: eventId
      });
      
      if (eventUsersError) {
        console.error('❌ Error with admin_get_event_users:', eventUsersError);
        // Fall back to original approach only if this fails
      } else if (eventUsers && eventUsers.length > 0) {
        console.log('✅ Successfully retrieved users for this event:', eventUsers.length);
        
        setUsers(eventUsers);
        setFilteredUsers(eventUsers);
        return;
      } else {
        console.log('No registered users found for this event.');
      }
      
      // If we get here, we need to fall back to older approaches
      
      // Try using the general admin_get_app_users RPC function first 
      console.log('Falling back to admin_get_app_users function...');
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('admin_get_app_users', {
        event_uuid: eventId
      });
      
      if (rpcError) {
        console.error('❌ Error using admin_get_app_users RPC:', rpcError);
        // Fall back to direct approaches if the RPC function fails
      } else if (rpcUsers && rpcUsers.length > 0) {
        console.log('✅ Retrieved general users via admin_get_app_users:', rpcUsers.length);
        
        // Now we need to filter this list to only those registered for this event
        const { data: eventOrders, error: eventOrdersError } = await supabase
          .from('event_orders')
          .select('user_id')
          .eq('event_id', eventId);
        
        if (eventOrdersError) {
          console.error('❌ Error fetching event orders for filtering:', eventOrdersError);
        } else if (eventOrders && eventOrders.length > 0) {
          console.log('Found orders to filter users:', eventOrders.length);
          
          // Get the user IDs from the orders
          const registeredUserIds = eventOrders.map(order => order.user_id);
          
          // Filter the users list to include only registered users
          const filteredUsersList = rpcUsers.filter(user => 
            registeredUserIds.includes(user.id) || user.event_id === eventId
          );
          
          console.log('Filtered users list to registered users:', filteredUsersList.length);
          
          if (filteredUsersList.length > 0) {
            setUsers(filteredUsersList);
            setFilteredUsers(filteredUsersList);
            return;
          }
        }
        
        // If we couldn't filter properly, use the full list as a fallback
        setUsers(rpcUsers);
        setFilteredUsers(rpcUsers);
        return;
      }
      
      // Fallback to checking event_orders directly if all else fails
      console.log('📋 Falling back to event_orders table only');
      
      const { data: eventOrders, error: eventOrdersError } = await supabase
        .from('event_orders')
        .select('user_id, answers, status, created_at')
        .eq('event_id', eventId);
      
      if (eventOrdersError) {
        console.error('❌ Error fetching event_orders:', eventOrdersError);
        throw eventOrdersError;
      }
      
      console.log('✅ Found orders in event_orders table:', eventOrders?.length || 0);
      if (eventOrders?.length > 0) {
        console.log('First order data sample:', eventOrders[0]);
      }
      
      if (eventOrders && eventOrders.length > 0) {
        // Get user details for all users registered for this event
        const userIds = eventOrders.map(order => order.user_id);
        console.log('Looking up user details for IDs:', userIds);
        
        const { data: userData, error: userError } = await supabase
          .from('app_users')
          .select('*')
          .in('id', userIds);
        
        if (userError) {
          console.error('❌ Error fetching user details:', userError);
          throw userError;
        }
        
        console.log('✅ Found user details for orders:', userData?.length || 0);
        if (userData?.length > 0) {
          console.log('First user detail sample:', userData[0]);
        } else {
          console.warn('⚠️ User details not found for order user IDs');
        }
        
        // Combine user data with order data
        let usersList = userData.map(user => {
          const order = eventOrders.find(o => o.user_id === user.id);
          return {
            ...user,
            order_status: order?.status || 'pending',
            answers: order?.answers || {},
            order_created_at: order?.created_at || null
          };
        });
        
        // If we found any users with this approach
        if (usersList.length > 0) {
          console.log('✅ Setting users list with real data from event_orders:', usersList.length);
          setUsers(usersList);
          setFilteredUsers(usersList);
          return;
        }
      }
      
      // No users found with any approach, fall back to mock data
      console.log('⚠️ No users found with any approach, using mock data');
      useMockEventUsers();
      
    } catch (error) {
      console.error('❌ Error fetching event users:', error);
      toast({
        title: 'Error loading users',
        description: `Could not load user data: ${error.message || 'Unknown error'}. Using sample data instead.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data
      useMockEventUsers();
    }
  };
  
  const useMockEventUsers = () => {
    const mockUsers = [
      {
        id: 'mock-user-1',
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-01T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-2',
        first_name: 'Jane',
        last_name: 'Smith',
        email: 'jane.smith@example.com',
        order_status: 'pending',
        user_type: 'customer',
        created_at: '2023-04-02T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-3',
        first_name: 'Michael',
        last_name: 'Johnson',
        email: 'michael.johnson@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-03T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-4',
        first_name: 'Emily',
        last_name: 'Williams',
        email: 'emily.williams@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-04T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-5',
        first_name: 'David',
        last_name: 'Brown',
        email: 'david.brown@example.com',
        order_status: 'pending',
        user_type: 'customer',
        created_at: '2023-04-05T00:00:00.000Z',
        profile_image: null
      }
    ];
    
    setUsers(mockUsers);
    setFilteredUsers(mockUsers);
  };
  
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredUsers(users);
      return;
    }
    
    const filtered = users.filter(user => 
      user.first_name.toLowerCase().includes(query) ||
      user.last_name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(query)
    );
    
    setFilteredUsers(filtered);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateString;
    }
  };
  
  // Navigate to user image upload
  const handleUploadImages = (userId) => {
    navigate(`/dashboard/events/${eventId}/users/${userId}/images`);
  };
  
  // Function to run diagnostics
  const runDiagnostics = async () => {
    try {
      console.log('Running diagnostics for event:', eventId);
      
      // Get debug information
      const { data, error } = await supabase.rpc('debug_event_users', {
        event_uuid: eventId
      });
      
      if (error) throw error;
      
      setDebugData(data);
      onOpen(); // Open the debug modal
      
    } catch (error) {
      console.error('Error running diagnostics:', error);
      toast({
        title: 'Diagnostics Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  if (loading) {
    return (
      <Flex justify="center" align="center" height="80vh" width="100%">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text ml={4} fontSize="xl">Loading event dashboard...</Text>
      </Flex>
    );
  }
  
  return (
    <Box className="event-dashboard-container" p={4}>
      <Link to="/dashboard/events">
        <Button 
          leftIcon={<FaAngleLeft />} 
          variant="ghost" 
          mb={4}
          size="sm"
        >
          Back to Events
        </Button>
      </Link>
      
      {event && (
        <>
          <Card 
            mb={6} 
            bg={headerBgColor} 
            color="white"
            borderRadius="lg"
            boxShadow="md"
            overflow="hidden"
          >
            <CardBody>
              <Flex justify="space-between" align="center" wrap="wrap">
                <Box>
                  <Heading size="lg">{event.name}</Heading>
                  <HStack mt={2}>
                    <FaCalendarAlt />
                    <Text>{formatDate(event.start_date)}{event.end_date && event.start_date !== event.end_date ? ` - ${formatDate(event.end_date)}` : ''}</Text>
                  </HStack>
                  <HStack mt={2}>
                    <FaMapMarkerAlt />
                    <Text>{event.city}, {event.state}</Text>
                  </HStack>
                </Box>
                <HStack spacing={4}>
                  <Box textAlign="center">
                    <Text fontSize="sm">Status</Text>
                    <Badge 
                      colorScheme={
                        event.status === 'active' ? 'green' : 
                        event.status === 'upcoming' ? 'yellow' :
                        event.status === 'completed' ? 'gray' : 'red'
                      }
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="sm"
                    >
                      {(event.status || 'PENDING').toUpperCase()}
                    </Badge>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="sm">Participants</Text>
                    <HStack align="center" justify="center">
                      <FaUserFriends />
                      <Text fontWeight="bold">{users.length}</Text>
                    </HStack>
                  </Box>
                  <Button
                    leftIcon={<FaEdit />}
                    colorScheme="whiteAlpha"
                    size="sm"
                    onClick={() => navigate(`/dashboard/events/${eventId}/edit`)}
                  >
                    Edit Event
                  </Button>
                  {isAdmin && (
                    <IconButton
                      icon={<FaBug />}
                      aria-label="Debug Event"
                      colorScheme="whiteAlpha"
                      size="sm"
                      onClick={runDiagnostics}
                      title="Show Debug Information"
                    />
                  )}
                </HStack>
              </Flex>
            </CardBody>
          </Card>
          
          {/* Debug modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Event Debug Information</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {debugData ? (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Source</Th>
                        <Th isNumeric>Count</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {debugData.map((item, index) => (
                        <Tr key={index}>
                          <Td>{item.source}</Td>
                          <Td isNumeric>{item.count}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Spinner />
                )}
                
                <Divider my={4} />
                
                <Box mt={4}>
                  <Heading size="sm" mb={2}>Event Details</Heading>
                  <Code p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap">
                    {JSON.stringify(event, null, 2)}
                  </Code>
                </Box>
                
                <Box mt={4}>
                  <Heading size="sm" mb={2}>Loaded Users Count: {users.length}</Heading>
                  <Text fontSize="sm" mb={2}>Event ID: {eventId}</Text>
                </Box>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" onClick={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          
          {/* Event Statistics Cards */}
          <Flex justifyContent="space-between" mb={6} flexWrap="wrap" gap={4}>
            <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
              <CardBody>
                <Flex direction="column" align="center" textAlign="center">
                  <Heading size="4xl" color="blue.500" mb={2}>
                    {users.length}
                  </Heading>
                  <Text fontWeight="medium">Total Registrations</Text>
                </Flex>
              </CardBody>
            </Card>
            
            <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
              <CardBody>
                <Flex direction="column" align="center" textAlign="center">
                  <Heading size="4xl" color="green.500" mb={2}>
                    {users.filter(user => user.order_status === 'completed').length}
                  </Heading>
                  <Text fontWeight="medium">Completed Orders</Text>
                </Flex>
              </CardBody>
            </Card>
            
            <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
              <CardBody>
                <Flex direction="column" align="center" textAlign="center">
                  <Heading size="4xl" color="yellow.500" mb={2}>
                    {users.filter(user => user.order_status === 'pending').length}
                  </Heading>
                  <Text fontWeight="medium">Pending Orders</Text>
                </Flex>
              </CardBody>
            </Card>
            
            <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
              <CardBody>
                <Flex direction="column" align="center" textAlign="center">
                  <Button colorScheme="blue" mb={2} borderRadius="full" size="lg" height="70px" width="70px">
                    <FaEnvelope size={24} />
                  </Button>
                  <Text fontWeight="medium">Send Mass Email</Text>
                </Flex>
              </CardBody>
            </Card>
          </Flex>
          
          <Tabs variant="enclosed" colorScheme="blue" borderRadius="md" boxShadow="sm" bg={bgColor}>
            <TabList>
              <Tab>Users</Tab>
              <Tab>Event Details</Tab>
              <Tab>Reports</Tab>
            </TabList>
            
            <TabPanels>
              <TabPanel>
                <Box mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FaSearch color="gray.300" />
                    </InputLeftElement>
                    <Input 
                      placeholder="Search users by name or email" 
                      value={searchQuery}
                      onChange={handleSearch}
                      borderRadius="md"
                    />
                  </InputGroup>
                </Box>
                
                {filteredUsers.length === 0 ? (
                  <Box textAlign="center" my={8} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                    <FaUserFriends size={40} style={{ margin: '0 auto', color: 'gray' }} />
                    <Text mt={4} fontSize="lg" fontWeight="medium">No users found</Text>
                    <Text color="gray.500">
                      {searchQuery ? 'Try a different search term' : 'There are no users registered for this event yet'}
                    </Text>
                  </Box>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple" borderRadius="md">
                      <Thead>
                        <Tr>
                          <Th>User</Th>
                          <Th>Contact Info</Th>
                          <Th>Registration Date</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredUsers.map(user => (
                          <Tr key={user.id}>
                            <Td>
                              <Flex align="center">
                                <Avatar 
                                  size="sm" 
                                  name={`${user.first_name} ${user.last_name}`} 
                                  src={user.profile_image} 
                                  mr={2} 
                                />
                                <Box>
                                  <Text fontWeight="medium">{user.first_name} {user.last_name}</Text>
                                  <Badge size="sm" colorScheme="purple" mt={1}>{user.user_type || 'customer'}</Badge>
                                </Box>
                              </Flex>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{user.email}</Text>
                              {user.phone && <Text fontSize="sm" color="gray.600">{user.phone}</Text>}
                            </Td>
                            <Td>
                              {user.created_at ? (
                                <Text fontSize="sm">{formatDate(user.created_at)}</Text>
                              ) : (
                                <Text fontSize="sm" color="gray.500">Unknown</Text>
                              )}
                            </Td>
                            <Td>
                              <Badge 
                                colorScheme={
                                  user.order_status === 'completed' ? 'green' : 
                                  user.order_status === 'pending' ? 'yellow' : 'gray'
                                }
                              >
                                {user.order_status?.toUpperCase()}
                              </Badge>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Upload images"
                                  icon={<FaCamera />}
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleUploadImages(user.id)}
                                  title="Upload images for this user"
                                />
                                <IconButton
                                  aria-label="Edit user"
                                  icon={<FaEdit />}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/dashboard/users/${user.id}/edit`)}
                                  title="Edit user details"
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="teal"
                                  onClick={() => navigate(`/dashboard/users/${user.id}`)}
                                >
                                  View Details
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </TabPanel>
              
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <Heading size="md">Event Information</Heading>
                  
                  <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    <Text fontWeight="bold">Description</Text>
                    <Text mt={2}>{event.description || 'No description provided'}</Text>
                  </Box>
                  
                  <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    <Text fontWeight="bold">Location</Text>
                    <Text mt={2}>
                      {event.address1}
                      {event.address2 && <>, {event.address2}</>}<br />
                      {event.city}, {event.state} {event.zip}
                    </Text>
                  </Box>
                  
                  <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    <Text fontWeight="bold">Event Type</Text>
                    <Badge mt={2} colorScheme="purple">{event.event_type || 'General'}</Badge>
                  </Box>
                </VStack>
              </TabPanel>
              
              <TabPanel>
                <Box textAlign="center" my={8} p={6}>
                  <Heading size="md">Reports & Analytics</Heading>
                  <Text mt={4} color="gray.500">This feature is coming soon.</Text>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </Box>
  );
};

export default EventDashboard; 